import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { UserPen } from "lucide-react";
import Navbar from "../../components/Navbar";

import { fetchArtistProfile } from "../../services/arthouseApi";
import ShopLinksPanel from "../../components/ArtHouse/ShopLinksPanel";

const MY_ARTIST_ID_KEY = "ARTHOUSE_MY_ARTIST_ID";

function safeText(v, fallback = "") {
  return (v ?? "").toString().trim() || fallback;
}

function safeUrl(u) {
  const s = String(u || "").trim();
  return s && (s.startsWith("http://") || s.startsWith("https://")) ? s : "";
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

function getArtworkImage(a) {
  return (
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    ""
  );
}

/** matches your ArtistMiniCard getArtworkSnapshot */
function getArtworkSnapshot(a) {
  if (!a) return null;

  const image_url =
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    "";

  return {
    id: a?.id ?? a?.artwork_id ?? a?.artworkId ?? null,
    title: a?.title || "Untitled",
    description: a?.description || a?.caption || a?.subtitle || "",
    image_url,
    tags: Array.isArray(a?.tags) ? a.tags : [],
    merchLinks: Array.isArray(a?.merchLinks) ? a.merchLinks : [],
    views: a?.views,
    likes: a?.likes,
    // if your API sometimes nests artist info on artwork, keep it
    artist: a?.artist || a?.creator || null,
  };
}

function IconLink({ href, label, children }) {
  const url = safeUrl(href);
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="
        inline-flex items-center gap-2
        rounded-full border border-white/10
        bg-white/[0.06] px-3 py-2
        text-xs font-semibold text-white/80
        hover:bg-white/[0.10] hover:border-white/20
        transition
      "
      aria-label={label}
      title={label}
    >
      {children}
      <span className="hidden sm:inline">{label}</span>
    </a>
  );
}

function TinyPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
      <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold text-white/90">{value}</div>
    </div>
  );
}

function ProfileTabs({ tab, setTab, counts }) {
  const item =
    "h-10 px-4 rounded-xl text-sm font-semibold border transition inline-flex items-center justify-center";
  const on =
    "border-sky-300/25 bg-sky-300/10 text-sky-200 shadow-[0_0_0_1px_rgba(125,211,252,0.14)]";
  const off =
    "border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/[0.08] hover:border-white/15";

  return (
    <div className="mt-5 flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => setTab("gallery")}
        className={`${item} ${tab === "gallery" ? on : off}`}
      >
        Gallery{" "}
        <span className="ml-2 text-xs text-white/45">({counts.posts})</span>
      </button>

      <button
        type="button"
        onClick={() => setTab("about")}
        className={`${item} ${tab === "about" ? on : off}`}
      >
        About
      </button>

      <button
        type="button"
        onClick={() => setTab("links")}
        className={`${item} ${tab === "links" ? on : off}`}
      >
        Links
      </button>
    </div>
  );
}

/**
 * Correct behavior (same concept as Gallery):
 * - Clicking an artwork navigates to /arthouse/artist-modal/:artistId
 * - Passes backgroundLocation + hubState + artwork snapshot
 * - Modal overlay is handled by your router setup (backgroundLocation pattern)
 */
function ArtworkIGGrid({ artworks = [], hubState, artistId }) {
  const location = useLocation();
  const navigate = useNavigate();

  function openArtistModal(artwork) {
    if (!artistId) return;

    navigate(`/arthouse/artist-modal/${artistId}`, {
      state: {
        backgroundLocation: location,
        ...(hubState || {}),
        artwork: getArtworkSnapshot(artwork),
      },
    });
  }

  return (
    <div
      className="
        mt-6
        grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3
        gap-5
      "
    >
      {artworks.map((a) => {
        const img = getArtworkImage(a);
        const title = safeText(a?.title, "Untitled");
        const id = getArtworkId(a);

        return (
          <button
            key={String(id ?? title) + String(img)}
            type="button"
            onClick={() => openArtistModal(a)}
            className="
              group relative rounded-[24px] overflow-hidden text-left
              border border-white/10
              bg-white/[0.045] backdrop-blur-xl
              shadow-[0_18px_80px_rgba(0,0,0,0.45)]
              transition
              hover:bg-white/[0.075]
              hover:shadow-[0_26px_120px_rgba(0,0,0,0.65)]
              hover:-translate-y-[2px]
              focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/40
            "
            title={title}
          >
            {/* subtle hover border glow */}
            <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
              <div className="absolute -inset-[1px] rounded-[26px] bg-gradient-to-br from-sky-300/20 via-transparent to-cyan-300/10" />
            </div>

            {/* image area (matches dashboard feel) */}
            <div className="relative aspect-[16/11] bg-black/25">
              {img ? (
                <img
                  src={img}
                  alt={title}
                  className="
                    absolute inset-0 h-full w-full object-cover
                    opacity-[0.94]
                    transition
                    group-hover:scale-[1.02]
                    group-hover:opacity-[0.98]
                    will-change-transform
                  "
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_35%_0%,rgba(56,189,248,0.14),rgba(0,0,0,0)_60%),radial-gradient(900px_480px_at_100%_20%,rgba(125,211,252,0.10),rgba(0,0,0,0)_55%)]" />
              )}

              {/* overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/18 to-black/28" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(255,255,255,0.10),transparent_55%)] opacity-60" />

              {/* top-left label + title (same info, better layout) */}
              <div className="absolute left-4 right-4 top-4">
                <div className="text-[10px] tracking-[0.34em] uppercase text-white/55">
                  Artwork
                </div>
                <div className="mt-1 font-semibold text-white/90 truncate">
                  {title}
                </div>
              </div>

              {/* bottom glass bar (no extra public info, just “View”) */}
             <div className="absolute left-4 right-4 bottom-4 rounded-2xl border border-white/10 bg-black/25 backdrop-blur-xl p-3">
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-3 text-xs text-white/70">
      <span className="tabular-nums">{a?.likes ?? 0}</span>
      <span className="tabular-nums">{a?.views ?? 0}</span>
      <span className="tabular-nums">{a?.comments ?? 0}</span>
    </div>

    <div className="opacity-0 group-hover:opacity-100 transition text-xs text-white/70">
      View{" "}
      <span className="inline-block translate-x-0 group-hover:translate-x-0.5 transition">
        →
      </span>
    </div>
  </div>
</div>

            </div>

            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
          </button>
        );
      })}
    </div>
  );
}


export default function ArtistProfilePage() {
  const { artistId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [tab, setTab] = useState("gallery");

  const myArtistId = localStorage.getItem(MY_ARTIST_ID_KEY);
  const isOwner = !!myArtistId && String(myArtistId) === String(artistId);

  // preserve hub state if we came from Hub
  const hubState = useMemo(() => {
    const incoming = location.state || {};
    return {
      ...incoming,
      hubSection: incoming.hubSection || "arthouse",
    };
  }, [location.state]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const d = await fetchArtistProfile(artistId); // { artist, artworks }
        if (!alive) return;
        setData(d);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load artist profile.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [artistId]);

  const artist = data?.artist || null;
  const artworks = useMemo(() => data?.artworks || [], [data]);

  const counts = useMemo(() => {
    const posts = (artworks || []).length;
    return { posts };
  }, [artworks]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070B16] text-white">
        <Navbar />
        <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 pb-14">
          <div className="rounded-[26px] border border-white/12 bg-white/[0.04] p-6 text-white/70">
            Loading…
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-[#070B16] text-white">
        <Navbar />

        <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 pb-14">
          <div className="rounded-[26px] border border-white/12 bg-white/[0.04] p-6">
            <div className="text-lg font-semibold text-white/90">
              Artist not found
            </div>
            <div className="mt-2 text-sm text-white/55">
              {errorMsg || "Unknown error."}
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="
                  h-10 px-4 rounded-xl
                  border border-white/12 bg-white/[0.04]
                  hover:bg-white/[0.08] transition
                  text-sm font-semibold text-white/75
                  inline-flex items-center justify-center
                "
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const avatarUrl = safeText(artist?.avatar_url, "");
  const pageBg = safeText(artist?.profile_bg_url, "");
  const bannerUrl = safeText(artist?.banner_url, "");

  const displayName = safeText(artist?.display_name, "Artist");
  const bio = safeText(artist?.bio, "");

  const social = {
    ig: safeText(artist?.instagram_url),
    tw: safeText(artist?.twitter_url),
    web: safeText(artist?.website_url),
  };

  return (
    <div className="min-h-screen bg-[#070B16] text-white relative">
      <Navbar />

      {/* background layer */}
      <div className="pointer-events-none fixed inset-0">
        {pageBg ? (
          <>
            <img
              src={pageBg}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-[0.26]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-[#070B16]/96" />
            <div className="absolute inset-0 [background:radial-gradient(1200px_520px_at_30%_10%,rgba(255,255,255,0.10),transparent_55%)] opacity-[0.30]" />
          </>
        ) : null}

        <div className="absolute -top-40 left-20 h-[520px] w-[520px] rounded-full bg-cyan-400/10 blur-[140px]" />
        <div className="absolute top-28 -right-44 h-[640px] w-[640px] rounded-full bg-sky-400/10 blur-[160px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(56,189,248,0.08),transparent_45%),radial-gradient(circle_at_80%_15%,rgba(125,211,252,0.06),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.0),rgba(7,11,22,0.8))]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1200px] px-4 pt-6 pb-14">
        {/* Top row */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="
              h-10 px-4 rounded-xl
              border border-white/12 bg-white/[0.04]
              hover:bg-white/[0.08] transition
              text-sm font-semibold text-white/75
              inline-flex items-center justify-center
            "
          >
            ← Back
          </button>

          {isOwner ? (
            <Link
              to="/dashboard/profile"
              state={hubState}
              className="
                h-10 px-4 rounded-xl
                border border-sky-300/25 bg-sky-300/10
                hover:bg-sky-300/15 transition
                text-sm font-semibold text-sky-200
                inline-flex items-center justify-center gap-2
              "
            >
              Go to My Profile
            </Link>
          ) : null}
        </div>

        {/* PROFILE HEADER */}
        <div className="mt-6 overflow-hidden rounded-[30px] border border-white/12 bg-white/[0.035] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.55)]">
          {/* banner */}
          <div className="relative h-[180px] sm:h-[220px] md:h-[260px] bg-black/25">
            {bannerUrl ? (
              <>
                <img
                  src={bannerUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-[0.92]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/25" />
              </>
            ) : (
              <>
                <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_35%_0%,rgba(56,189,248,0.18),rgba(0,0,0,0)_60%),radial-gradient(900px_480px_at_100%_20%,rgba(125,211,252,0.12),rgba(0,0,0,0)_55%)]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/25" />
              </>
            )}

            <div className="pointer-events-none absolute -top-20 left-1/2 h-56 w-[840px] -translate-x-1/2 rounded-full bg-white/10 blur-[40px] opacity-[0.18]" />
          </div>

          {/* body */}
          <div className="relative p-5 sm:p-6">
            <div className="flex items-start justify-between gap-5 flex-wrap">
              <div className="flex items-start gap-4">
                {/* avatar + profile button */}
                <div className="-mt-14 sm:-mt-16 shrink-0 flex flex-col items-center gap-3">
                  <div className="h-[92px] w-[92px] sm:h-[108px] sm:w-[108px] rounded-[26px] overflow-hidden border border-white/15 bg-black/30 shadow-[0_18px_60px_rgba(0,0,0,0.55)]">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="avatar"
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-[radial-gradient(circle_at_30%_20%,rgba(56,189,248,0.20),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(125,211,252,0.12),transparent_60%)]" />
                    )}
                  </div>

                  {isOwner && (
                    <Link
                      to="/profilehub"
                      className="
                        flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                        border border-white/12 bg-white/[0.06]
                        hover:bg-white/[0.10] hover:border-white/20
                        transition-all duration-300 ease-out
                        text-sm font-semibold text-white/85
                        hover:scale-105 active:scale-95
                      "
                    >
                      <UserPen size={16} />
                      AniProfile
                    </Link>
                  )}
                </div>

                <div className="pt-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-xl sm:text-2xl font-semibold text-white/92 truncate">
                      {displayName}
                    </div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-white/45">
                      Artist Profile
                    </div>
                  </div>

                  {bio ? (
                    <div className="mt-2 text-sm text-white/65 leading-relaxed max-w-[62ch]">
                      {bio}
                    </div>
                  ) : (
                    <div className="mt-2 text-sm text-white/45">
                      This artist hasn’t written a bio yet.
                    </div>
                  )}

                  {/* social chips */}
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <IconLink href={social.ig} label="Instagram">
                      <span className="text-white/70">⌁</span>
                    </IconLink>
                    <IconLink href={social.tw} label="X / Twitter">
                      <span className="text-white/70">⌁</span>
                    </IconLink>
                    <IconLink href={social.web} label="Website">
                      <span className="text-white/70">⌁</span>
                    </IconLink>
                  </div>
                </div>
              </div>

              {/* stats + actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <TinyPill label="Posts" value={counts.posts} />
                <button
                  type="button"
                  onClick={() => setTab("gallery")}
                  className="
                    h-[46px] px-5 rounded-2xl
                    border border-white/10 bg-white/[0.05]
                    hover:bg-white/[0.09] hover:border-white/15
                    transition
                    text-sm font-semibold text-white/80
                    inline-flex items-center justify-center
                  "
                >
                  View Public Gallery
                </button>
              </div>
            </div>

            {/* tabs */}
            <ProfileTabs tab={tab} setTab={setTab} counts={counts} />

            {/* tab content */}
            {tab === "gallery" ? (
              <>
                <ArtworkIGGrid
                  artworks={artworks}
                  hubState={hubState}
                  artistId={artistId}
                />

                {artworks.length === 0 ? (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-white/60">
                    No artworks yet.
                  </div>
                ) : null}
              </>
            ) : tab === "about" ? (
              <div className="mt-6 grid gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-sm font-semibold text-white/85">About</div>
                  <div className="mt-2 text-sm text-white/60 leading-relaxed">
                    {bio || "No bio yet."}
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <TinyPill
                      label="Instagram"
                      value={social.ig ? "Connected" : "—"}
                    />
                    <TinyPill
                      label="X / Twitter"
                      value={social.tw ? "Connected" : "—"}
                    />
                    <TinyPill
                      label="Website"
                      value={social.web ? "Connected" : "—"}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-sm font-semibold text-white/85">
                    Store + Support
                  </div>
                  <div className="mt-2 text-sm text-white/60">
                    Artist links so people can support / buy.
                  </div>
                  <div className="mt-4">
                    <ShopLinksPanel artist={artist} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-sm font-semibold text-white/85">Links</div>
                <div className="mt-2 text-sm text-white/60">
                  Social + store links in one place.
                </div>
                <div className="mt-4">
                  <ShopLinksPanel artist={artist} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
